import { memo } from 'react';

import { type PublicClip } from '@air/api';
import { type Clip } from '@air/api/types';
import { Tooltip } from '@air/primitive-tooltip';

const size = 16;
const padding = 3.33;
const availableWidth = size - padding * 2;
const maximumStrength = 4;
const barMinimumHeight = availableWidth * 0.33;
const barMaximumHeight = availableWidth * 1;
const barWidth = availableWidth / (maximumStrength + (maximumStrength - 1));

export type RelevanceIndicatorProps = {
  strength: number;
};

const RelevanceIndicator = memo(({ strength }: RelevanceIndicatorProps) => (
  <svg aria-hidden="true" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
    {Array.from({ length: maximumStrength }, (_, index) => {
      const x = index * (barWidth * 2) + padding;
      const y = padding;
      const height =
        index < strength
          ? barMinimumHeight + index * ((barMaximumHeight - barMinimumHeight) / maximumStrength)
          : barWidth;

      return (
        <rect
          key={index}
          x={x}
          y={size - y - height}
          width={barWidth}
          height={height}
          rx={barWidth / 2}
          ry={barWidth / 2}
          fill="white"
        />
      );
    })}
  </svg>
));

RelevanceIndicator.displayName = 'RelevanceIndicator';

export type GallerySmartSearchRelevanceIndicatorProps = {
  clip: Clip | PublicClip;
};

export const GallerySmartSearchRelevanceIndicator = memo(({ clip }: GallerySmartSearchRelevanceIndicatorProps) => {
  if (!clip.relevance) {
    return null;
  }

  const clipRelevance = clip.relevance;

  // Relevance is a number between 0 and 1
  // We need to convert it to a number between 0 and 4, primarily graded on a curve of y² = mx + b
  // Using piecewise function: quadratic curve for 0-0.9, then cap at 4 for >0.9
  // Strength 0: 0 ≤ clipRelevance < ~0.056
  // Strength 1: ~0.056 ≤ clipRelevance < ~0.225
  // Strength 2: ~0.225 ≤ clipRelevance < ~0.506
  // Strength 3: ~0.506 ≤ clipRelevance < 0.9
  // Strength 4: clipRelevance ≥ 0.9

  const strength = clipRelevance > 0.9 ? maximumStrength : Math.floor(maximumStrength * Math.sqrt(clipRelevance / 0.9));

  return (
    <Tooltip label={`Relevance: ${(clipRelevance * 100).toFixed(1)}%`}>
      <div className="pointer-events-auto rounded-4 bg-purple-10 p-1">
        <RelevanceIndicator strength={strength} />
      </div>
    </Tooltip>
  );
});

GallerySmartSearchRelevanceIndicator.displayName = 'GallerySmartSearchRelevanceIndicator';
